import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Check, Sparkles } from 'lucide-react';
import { Card, CardDivider } from '@/components/ui/Card';
import { Icon } from '@/components/ui/Icon';
import { cn } from '@/lib/cn';

/**
 * Design system §12. Skeletons for content under 2s; a spinner plus a sentence
 * for anything that may take longer. Empty states name the next action.
 */

export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        'shrink-0 rounded-md bg-[linear-gradient(90deg,#EEEFEC_0px,#F6F4EF_50%,#EEEFEC_100%)] bg-[length:800px_100%] [animation:ds-shimmer_1.4s_linear_infinite]',
        className,
      )}
    />
  );
}

export function Spinner({
  size = 18,
  className,
  label,
}: {
  size?: number;
  className?: string;
  label?: string;
}) {
  return (
    <span
      role={label ? 'status' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : 'true'}
      style={{ width: size, height: size }}
      className={cn(
        'inline-block shrink-0 rounded-full border-2 border-border border-t-primary [animation:ds-spin_0.8s_linear_infinite]',
        className,
      )}
    />
  );
}

/** Spinner plus one sentence — for waits that may exceed two seconds. */
export function LoadingLine({ children }: { children: ReactNode }) {
  return (
    <p role="status" className="flex items-center gap-2.5 text-[15px] text-ink-2">
      <Spinner />
      {children}
    </p>
  );
}

/** Card-shaped skeleton: avatar tile, two lines of heading, two body lines. */
export function SkeletonCard({ className }: { className?: string }) {
  return (
    <Card padding="lg" className={cn('grid gap-3.5', className)}>
      <div className="flex items-center gap-3">
        <Skeleton className="size-12 rounded-[12px]" />
        <div className="grid flex-1 gap-2">
          <Skeleton className="h-3.5 w-7/10" />
          <Skeleton className="h-3 w-[45%]" />
        </div>
      </div>
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-[85%]" />
    </Card>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <Card
      padding="none"
      className={cn(
        'grid place-content-center justify-items-center gap-3 px-6 py-10 text-center',
        className,
      )}
    >
      <span className="grid size-14 place-items-center rounded-[16px] bg-primary-tint text-primary">
        <Icon icon={icon} size={24} />
      </span>
      <h3 className="font-display text-[20px] font-semibold">{title}</h3>
      <p className="max-w-70 text-[15px] text-muted">{description}</p>
      {action ? <div className="mt-1">{action}</div> : null}
    </Card>
  );
}

/** Indeterminate bar for work with no measurable progress. */
export function IndeterminateBar({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={cn('relative h-1.5 overflow-hidden rounded-[3px] bg-primary-tint', className)}
    >
      <div className="absolute inset-y-0 w-[35%] rounded-[3px] bg-primary [animation:ds-indeterminate_1.4s_ease-in-out_infinite]" />
    </div>
  );
}

export interface AiProcessingStep {
  readonly label: string;
  readonly state: 'done' | 'active' | 'pending';
}

/**
 * AI-processing panel — shown while an evaluation job runs.
 * Steps are the rubric criteria being scored; `footnote` states the cost in credits.
 */
export function AiProcessing({
  title,
  description,
  steps,
  footnote,
  className,
}: {
  title: string;
  description: string;
  steps: readonly AiProcessingStep[];
  footnote?: string;
  className?: string;
}) {
  return (
    <Card padding="lg" className={cn('grid gap-4', className)}>
      <div className="flex items-center gap-3">
        <span className="grid size-11 shrink-0 place-items-center rounded-[12px] bg-primary-tint text-primary">
          <Icon icon={Sparkles} size={22} />
        </span>
        <div>
          <h3 className="font-display text-[19px] font-semibold leading-tight">{title}</h3>
          <p className="text-small text-muted">{description}</p>
        </div>
      </div>

      <IndeterminateBar />

      <ol className="grid gap-2.5" aria-live="polite">
        {steps.map((step) => (
          <li
            key={step.label}
            className={cn(
              'flex items-center gap-2.5 text-[15px]',
              step.state === 'active' ? 'font-bold text-ink' : 'text-ink',
              step.state === 'pending' && 'text-muted',
            )}
          >
            <span className="grid size-5.5 shrink-0 place-items-center">
              {step.state === 'done' ? (
                <span className="grid size-5 place-items-center rounded-full bg-primary text-white">
                  <Icon icon={Check} size={12} />
                </span>
              ) : step.state === 'active' ? (
                <Spinner size={18} />
              ) : (
                <span className="size-4 rounded-full border-2 border-border" />
              )}
            </span>
            {step.label}
          </li>
        ))}
      </ol>

      {footnote ? (
        <>
          <CardDivider />
          <p className="text-[13px] text-muted">{footnote}</p>
        </>
      ) : null}
    </Card>
  );
}
